import { useMemo, useState } from "react";
import { Button, Collapse, Empty, Input, Tag, Tooltip, Tree, Typography } from "antd";
import { HolderOutlined, PlusOutlined } from "@ant-design/icons";
import { useDraggable } from "@dnd-kit/core";

import { sourceTypeTag } from "./structure";
import styles from "./DocumentBuilder.module.css";

const { Text } = Typography;

export const sourceDragId = (documentId, nodeId) => `source::${documentId}::${nodeId}`;

const matches = (node, query) => node.title.toLowerCase().includes(query);

/** Keep the nodes that match, plus the ancestors needed to reach them. */
const filterNodes = (nodes, query) =>
  nodes
    .map((node) => {
      const children = filterNodes(node.children, query);
      if (children.length || matches(node, query)) return { ...node, children };
      return null;
    })
    .filter(Boolean);

const OutlineNode = ({ node, addHint, onAdd }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: sourceDragId(node.documentId, node.id),
    data: {
      kind: "source",
      source: {
        documentId: node.documentId,
        filename: node.filename,
        nodeId: node.id,
        label: node.title,
        type: node.type,
        level: node.level,
        blockCount: node.blockCount
      }
    }
  });

  const tag = sourceTypeTag(node);

  return (
    <div className={styles.outlineNode} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <div ref={setNodeRef} {...listeners} {...attributes} className={styles.outlineGrab}>
        <HolderOutlined className={styles.grip} />
        <Tag color={tag.color} className={styles.levelTag}>
          {tag.label}
        </Tag>
        <Tooltip title={node.preview || node.title} mouseEnterDelay={0.6}>
          <span className={styles.outlineLabel}>{node.title}</span>
        </Tooltip>
        <Text type="secondary" className={styles.blockCount}>
          {node.blockCount}
        </Text>
      </div>
      <Tooltip title={addHint}>
        <Button type="text" size="small" icon={<PlusOutlined />} onClick={() => onAdd(node)} onPointerDown={(event) => event.stopPropagation()} />
      </Tooltip>
    </div>
  );
};

const toTreeData = (nodes, document) =>
  nodes.map((node) => ({
    key: `${document.documentId}::${node.id}`,
    title: node.title,
    node: { ...node, documentId: document.documentId, filename: document.filename },
    children: toTreeData(node.children, document)
  }));

/**
 * The outline of every loaded document: Heading 1-9 structure, content
 * controls, and for documents without headings, the extracted tables and
 * text. Each row is draggable into a section of the document being assembled.
 */
const OutlineTree = ({ documents, addHint, onAdd }) => {
  const [query, setQuery] = useState("");
  const trimmed = query.trim().toLowerCase();

  const panels = useMemo(
    () =>
      documents.map((document) => {
        const nodes = trimmed ? filterNodes(document.outline.nodes, trimmed) : document.outline.nodes;
        return { document, treeData: toTreeData(nodes, document), total: nodes.length };
      }),
    [documents, trimmed]
  );

  if (!documents.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Add documents on the left to see their sections here
          </Text>
        }
      />
    );
  }

  return (
    <div className={styles.outlineWrapper}>
      <Input.Search
        allowClear
        size="small"
        placeholder="Find a section"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <Collapse
        size="small"
        ghost
        defaultActiveKey={documents.length === 1 ? [documents[0].documentId] : []}
        items={panels.map(({ document, treeData, total }) => ({
          key: document.documentId,
          label: (
            <Text ellipsis style={{ fontSize: 12, maxWidth: "100%" }}>
              {document.filename}
            </Text>
          ),
          extra: (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {total}
            </Text>
          ),
          children: treeData.length ? (
            <Tree
              blockNode
              selectable={false}
              treeData={treeData}
              // Remounting on a new search term re-applies the expansion, so
              // matches deep in a document are visible without hunting.
              key={trimmed}
              defaultExpandAll={Boolean(trimmed) || treeData.some((item) => item.node && item.node.type !== "heading" && item.node.type !== "contentControl")}
              className={styles.tree}
              titleRender={(item) => <OutlineNode node={item.node} addHint={addHint} onAdd={onAdd} />}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {trimmed ? "No matching content" : "No extractable content"}
            </Text>
          )
        }))}
      />
    </div>
  );
};

export default OutlineTree;
